import * as React from 'react';
import cx from 'classnames';
import { object } from 'prop-types';
import styles from './Image.css';
import shallowEqual from 'utils/shallowEqual';
import Lazyload from 'utils/lazyloadUtil';
import placeholderImg from 'images/placeholder.svg';
import { addClass, loadImage } from 'utils/domHelper';
export default class Image extends React.Component {
    constructor() {
        super(...arguments);
        this.registerLazy = (props = this.props) => {
            if (!this.imgRef)
                return;
            const container = props.modalContainerId ? document.getElementById(props.modalContainerId) : null;
            const imgSrc = props.src;
            const loadedClass = styles['simple-image-loaded'];
            const loadedCallback = () => {
                // Unregistering here because otherwise this routine gets called on
                // every scroll. The CSS class would get toggled everytime this
                // happens, the effect of which is not consistent across browsers,
                // not to mention extra jank.
                //
                // TODO: Do we have to set this.lazyId to null here so that other
                // unregister calls don't happen? There won't be any error when we
                // try to unregister twice, but why even do it.
                // TODO: Move this check into lazyload. Basically, the register
                // method here should just be a one time binding.
                this.lazyId && Lazyload.unregister(this.lazyId);
                typeof props.onLoad === 'function' && props.onLoad(this.imgRef);
                return loadImage(this.imgRef, imgSrc, placeholderImg, props.highResSrc, loadedClass)();
            };
            // TODO(@kgrz): As a hack, we're checking if the image is a
            // primaryImage, and calling the lazyload functions without doing an
            // actual lazyload. This is because the entire PrimaryImage zoom
            // functionality relies on a callback on image load, but it won't work
            // when we directly pass the onload callback to the underlying image
            // prop due to browser caching. So when `props.primaryImage` is true,
            // the src is set to the main image directly since we don't use
            // lazyloading there, but the callbacks need to be registered
            // here. Actual fix would be to make sure the callbacks are put
            // in componentDidMount
            if (props.lazyLoadEnable || props.primaryImage) {
                this.lazyId = Lazyload.register(this.imgRef, loadedCallback, container ? container : undefined);
                this.imgRef.onload = () => {
                    if (!this.imgRef)
                        return;
                    typeof props.onLoad === 'function' && props.onLoad(this.imgRef);
                    this.imgRef && addClass(this.imgRef, loadedClass);
                };
                this.imgRef.onerror = e => {
                    if (!this.imgRef)
                        return;
                    // resetting srcset and setting src to show placeholder image
                    this.imgRef.src = placeholderImg;
                    this.imgRef.srcset = '';
                    typeof props.onError === 'function' && props.onError(e);
                    this.imgRef && addClass(this.imgRef, loadedClass);
                };
            }
        };
    }
    componentDidMount() {
        this.registerLazy();
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!shallowEqual(this.props, nextProps)) {
            this.lazyId && Lazyload.unregister(this.lazyId);
            this.registerLazy(nextProps);
        }
    }
    componentWillUnmount() {
        this.lazyId && Lazyload.unregister(this.lazyId);
    }
    shouldComponentUpdate(nextProps) {
        return !shallowEqual(this.props, nextProps);
    }
    render() {
        const { lazyLoadEnable, src, highResSrc, alt, imgClassName, imgStyle = {}, containerClass, setRef } = this.props;
        const imgSrc = src || placeholderImg;
        const height = this.props.actualSize || this.props.height || this.props.size;
        const width = this.props.actualSize || this.props.width || this.props.size;
        const containerHeight = typeof height === 'undefined' ? 'inherit' : `${height}px`;
        const containerWidth = typeof width === 'undefined' ? 'inherit' : `${width}px`;
        const imgProps = {
            ref: ref => {
                this.imgRef = ref;
                typeof setRef === 'function' && setRef(ref);
            },
            style: imgStyle,
            className: cx(styles['simple-image'], imgClassName, { [styles['simple-image-loaded']]: !lazyLoadEnable }),
            alt,
            src: lazyLoadEnable ? placeholderImg : imgSrc
        };
        const classNames = cx(styles['simple-image-container'], containerClass);
        // If lazy load is enabled, the flow to update the url of the image tag
        // is handled inside the LazyLoad Intersection Observer handler.
        if (highResSrc && !lazyLoadEnable) {
            const srcSet = `${highResSrc} 2x, ${imgProps.src} 1x`;
            Object.assign(imgProps, { srcSet });
        }
        return (<div className={classNames} style={{ height: containerHeight, width: containerWidth }}>
				<img {...imgProps}/>
			</div>);
    }
}
Image.contextTypes = {
    location: object
};
Image.defaultProps = {
    alt: '',
    lazyLoadEnable: true,
    primaryImage: false
};
